import React, { useState, useEffect } from "react";
import { Typography, Box, Snackbar, Alert } from "@mui/material";
import { Button, TextField, Container } from "@/components/atoms";
import { isValidEmail, isValidPassword } from "@/utils/validators";
import { useCreateUser } from "@/hooks";
import GoogleIcon from "@mui/icons-material/Google";

export const Register = () => {
  const { data, loading, error, createUser } = useCreateUser();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");

  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordConfirmationError, setPasswordConfirmationError] = useState<
    string | null
  >(null);

  const [isFormValid, setIsFormValid] = useState(false);
  const [snackBar, setSnackBar] = useState({
    state: false,
    msg: "",
    severity: "info" as "error" | "success" | "info",
  });

  useEffect(() => {
    setEmailError(
      email && !isValidEmail(email) ? "Invalid email address" : null,
    );
    setPasswordError(
      password && !isValidPassword(password)
        ? "Password must be at least 8 characters."
        : null,
    );
    setPasswordConfirmationError(
      password && passwordConfirmation && password !== passwordConfirmation
        ? "Passwords do not match."
        : null,
    );
  }, [email, password, passwordConfirmation]);

  useEffect(() => {
    setIsFormValid(
      !emailError &&
        !passwordError &&
        !passwordConfirmationError &&
        email !== "" &&
        password !== "" &&
        passwordConfirmation !== "",
    );
  }, [
    emailError,
    passwordError,
    passwordConfirmationError,
    email,
    password,
    passwordConfirmation,
  ]);

  useEffect(() => {
    if (data?.success) {
      setSnackBar({
        state: true,
        msg: "User registration successful. Redirecting...",
        severity: "success",
      });
      setTimeout(() => {
        window.location.href = "/login";
      }, 1000);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      setSnackBar({
        state: true,
        msg: error,
        severity: "error",
      });
    }
  }, [error]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    await createUser({
      email: email.trim(),
      password,
      confirm_password: passwordConfirmation,
      first_name: firstName.trim(),
      last_name: lastName.trim(),
    });
  };

  return (
    <Container
      componentName="register"
      sx={{
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Snackbar
        open={snackBar.state}
        autoHideDuration={6000}
        onClose={() => setSnackBar({ ...snackBar, state: false })}
        message={snackBar.msg}
      >
        <Alert
          severity={snackBar.severity}
          onClose={() => setSnackBar(prev => ({ ...prev, state: false }))}
          variant="filled"
        >
          {snackBar.msg}
        </Alert>
      </Snackbar>
      <Box
        justifyContent="center"
        display="flex"
        sx={{ margin: 2 }}
        data-testid="register-title"
      >
        <Typography variant="h4" gutterBottom>
          Register
        </Typography>
      </Box>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          width: "100%",
          maxWidth: "400px",
          border: "none",
        }}
        data-testid="register-form"
      >
        <Box columnGap={2} sx={{ display: "flex", flexDirection: "row" }}>
          <TextField
            componentName="register-first-name"
            placeholder="First Name"
            variant="outlined"
            value={firstName}
            label="First Name"
            onChange={e => setFirstName(e.target.value)}
            required
            fullWidth
          />
          <TextField
            componentName="register-last-name"
            placeholder="Last Name"
            variant="outlined"
            value={lastName}
            label="Last Name"
            onChange={e => setLastName(e.target.value)}
            required
            fullWidth
          />
        </Box>
        <TextField
          componentName="register-email"
          placeholder="Email"
          variant="outlined"
          value={email}
          label="Email"
          onChange={e => setEmail(e.target.value)}
          error={!!emailError}
          helperText={emailError}
          required
          fullWidth
        />
        <TextField
          componentName="register-password"
          type="password"
          variant="outlined"
          placeholder="Password"
          value={password}
          label="Password"
          onChange={e => setPassword(e.target.value)}
          required
          error={!!passwordError}
          helperText={passwordError}
          fullWidth
        />
        <TextField
          componentName="register-password-confirmation"
          type="password"
          variant="outlined"
          placeholder="Confirm Password"
          value={passwordConfirmation}
          label="Confirm Password"
          onChange={e => setPasswordConfirmation(e.target.value)}
          required
          error={!!passwordConfirmationError}
          helperText={passwordConfirmationError}
          fullWidth
        />
        <Button
          componentName="register-button"
          color="primary"
          type="submit"
          disabled={!isFormValid || loading}
          loading={loading}
        >
          Register
        </Button>
      </Box>
      <Box
        flexDirection="row"
        display="flex"
        columnGap={2}
        justifyContent="flex-start"
        sx={{ mt: 1 }}
        data-testid="register-links"
      >
        <Typography>
          <a href="/login">Already have an account? Login</a>
        </Typography>
      </Box>

      {/* Google SSO */}
      <Typography sx={{ mt: 4 }}>OR</Typography>
      <Button
        componentName="google-register-button"
        variant="outlined"
        startIcon={<GoogleIcon />}
        color="primary"
        sx={{ my: 2 }}
        onClick={() => {
          const backendBase = import.meta.env.VITE_API_BASE_URL;
          if (!backendBase) {
            console.error("Missing VITE_API_BASE_URL in environment variables");
            return;
          }

          window.location.assign(`${backendBase}/login/google-oauth2/`);
        }}
      >
        Continue with Google
      </Button>
    </Container>
  );
};
